// 회사 등록 폼

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import DaumPostcode from "../widgets/DaumPostcode";
import FactoryRow from "./FactoryRow";

import { listCompanies } from "../../api/users/companies";
import { listRoles } from "../../api/users/roles";
import { listPCP, createPCP, deletePCP } from "../../api/users/pcp";

const PHONE_PREFIXES = [
  "010",
  "011",
  "016",
  "017",
  "018",
  "019",
  "02",
  "031",
  "032",
  "033",
  "041",
  "042",
  "043",
  "044",
  "051",
  "052",
  "053",
  "054",
  "055",
  "061",
  "062",
  "063",
  "064",
  "070",
  "0505",
  "1588",
  "1577",
  "1661",
  "1522",
];

const EMAIL_DOMAINS = [
  "gmail.com",
  "naver.com",
  "daum.net",
  "kakao.com",
  "nate.com",
  "hotmail.com",
  "icloud.com",
  "__custom__",
];

const digitsOnly = (v) => (v || "").replace(/\D/g, "");
function formatRestByPrefix(prefix, restDigitsRaw) {
  const rest = digitsOnly(restDigitsRaw).slice(0, 8);
  if (rest.length <= 3) return rest;
  if (rest.length <= 7) return `${rest.slice(0, 3)}-${rest.slice(3)}`;
  return `${rest.slice(0, 4)}-${rest.slice(4, 8)}`;
}

const EMPTY_PCP = {
  company: "",
  participant_role: "",
  is_lead: false,
  start_date: "",
  end_date: "",
};

const CompanyForm = ({
  newCompany,
  setNewCompany,
  hasFactories,
  setHasFactories,
  factoryRows,
  setFactoryRows,
  onCreate,
  onUpdate,
  onDelete,
  projectPeriod,
  EMPTY_COMPANY,
  EMPTY_FACTORY_ROW,
}) => {
  const { id: projectId } = useParams();

  // ---------------- PCP 상태 ----------------
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pcpRows, setPcpRows] = useState([]);
  const [pcpForm, setPcpForm] = useState({ ...EMPTY_PCP });
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [roleToAdd, setRoleToAdd] = useState("");
  const [roleLeadFlag, setRoleLeadFlag] = useState(false);

  const canCreatePCP = !!(
    projectId &&
    pcpForm.company &&
    pcpForm.participant_role
  );

  // ---------------- 회사 폼 ----------------
  const [phonePrefix, setPhonePrefix] = useState("010");
  const [phoneRest, setPhoneRest] = useState("");
  const [emailLocal, setEmailLocal] = useState("");
  const [emailDomainSel, setEmailDomainSel] = useState("gmail.com");
  const [emailDomainCustom, setEmailDomainCustom] = useState("");

  const [address, setAddress] = useState("");
  const [extraAddress, setExtraAddress] = useState("");

  const emailDomain =
    emailDomainSel === "__custom__" ? emailDomainCustom.trim() : emailDomainSel;
  const displayedRest = formatRestByPrefix(phonePrefix, phoneRest);

  const onChangeNew = (key) => (e) =>
    setNewCompany((prev) => ({ ...prev, [key]: e.target.value }));

  // ---------------- PCP 목록/옵션 로딩 ----------------
  const loadPCP = async () => {
    if (!projectId) return;
    const res = await listPCP({ project: projectId, page_size: 1000 });
    setPcpRows(res.data?.results ?? res.data ?? []);
  };

  useEffect(() => {
    const loadOptions = async () => {
      const [cRes, rRes] = await Promise.all([
        listCompanies({ page_size: 1000 }),
        listRoles({ project: projectId, page_size: 1000 }),
      ]);
      setCompanies(cRes.data?.results ?? cRes.data ?? []);
      setRoles(rRes.data?.results ?? rRes.data ?? []);
    };
    loadOptions();
  }, [projectId]);

  useEffect(() => {
    loadPCP();
  }, [projectId]);

  const createPcpHandler = async () => {
    if (!canCreatePCP) {
      alert("회사와 역할을 선택하세요.");
      return;
    }
    // 같은 회사+역할 중복 체크
    const dup = pcpRows.some(
      (r) =>
        (r.company === pcpForm.company || r.company_id === pcpForm.company) &&
        (r.participant_role === pcpForm.participant_role ||
          r.participant_role_id === pcpForm.participant_role)
    );
    if (dup) {
      alert("이미 같은 회사와 역할로 등록되어 있습니다.");
      return;
    }

    const body = {
      project: projectId, // 문자열(PJT001 등)
      company: Number(pcpForm.company),
      participant_role: Number(pcpForm.participant_role),
      is_lead: !!pcpForm.is_lead,
      start_date: pcpForm.start_date || null,
      end_date: pcpForm.end_date || null,
    };

    try {
      await createPCP(body);
      setPcpForm({ ...EMPTY_PCP });
      await loadPCP();
    } catch (e) {
      if (e?.response) {
        alert(
          `등록 실패(${e.response.status}):\n` +
            JSON.stringify(e.response.data, null, 2)
        );
      } else {
        alert("등록 실패: " + e.message);
      }
    }
  };

  const removePcp = async (id) => {
    if (!window.confirm("이 참여사 매핑을 삭제하시겠습니까?")) return;
    await deletePCP(id);
    await loadPCP();
  };

  // ---------------- 회사 폼 초기화 및 제출 ----------------
  useEffect(() => {
    if (newCompany.id) {
      // 이메일 분리
      if (newCompany.email) {
        const [local, domain] = newCompany.email.split("@");
        setEmailLocal(local);

        if (EMAIL_DOMAINS.includes(domain)) {
          setEmailDomainSel(domain);
          setEmailDomainCustom("");
        } else {
          setEmailDomainSel("__custom__");
          setEmailDomainCustom(domain);
        }
      }

      // 전화번호 분리
      if (newCompany.phone) {
        const [prefix, ...restParts] = newCompany.phone.split("-");
        setPhonePrefix(prefix || "010");
        setPhoneRest(restParts.join("") || "");
      }
    }
  }, [newCompany]);

  // ---------------- 회사 폼 제출 ----------------
  const handleSubmit = () => {
    if (!newCompany.name.trim()) {
      alert("회사명을 입력하세요.");
      return;
    }
    if (!(newCompany.address || "").trim()) {
      alert("주소를 입력하세요.");
      return;
    }
    if (emailLocal && !emailDomain) {
      alert("이메일 도메인을 선택/입력하세요.");
      return;
    }
    if (selectedRoles.length === 0) {
      alert("프로젝트 참여자 역할을 선택하세요.");
      return;
    }

    const payloadCompany = { ...newCompany };
    if (displayedRest) payloadCompany.phone = `${phonePrefix}-${displayedRest}`;
    if (emailLocal && emailDomain)
      payloadCompany.email = `${emailLocal}@${emailDomain}`;
    else delete payloadCompany.email;

    const payloadFactories = hasFactories
      ? factoryRows.filter((r) => (r.name || "").trim())
      : [];

    if (newCompany.id) {
      onUpdate(payloadCompany); // 수정 모드
    } else {
      // 회사 + 역할 + 프로젝트 기간 정보 포함한 최종 payload
      const companyWithRoles = {
        ...payloadCompany,
        project: projectId,
        roles: selectedRoles.map((r) => ({
          participant_role: r.id,
          is_lead: r.is_lead,
          start_date: pcpForm.start_date || null,
          end_date: pcpForm.end_date || null,
        })),
      };

      onCreate({
        company: companyWithRoles,
        factories: payloadFactories,
      });
    }

    resetForm(); // 입력 필드 초기화
  };

  const addFactoryRow = () =>
    setFactoryRows((prev) => [
      ...prev,
      {
        ...EMPTY_FACTORY_ROW,
        useCopyAddress: false,
        useSearch: true,
      },
    ]);
  const removeFactoryRow = (idx) =>
    setFactoryRows((prev) => prev.filter((_, i) => i !== idx));
  const onChangeFactoryRow = (idx, key, value) =>
    setFactoryRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r))
    );

  const resetForm = () => {
    setNewCompany(EMPTY_COMPANY); // 주소, 상세 주소 초기화
    setPhonePrefix("010");
    setPhoneRest("");
    setEmailLocal("");
    setEmailDomainSel(EMAIL_DOMAINS[0]);
    setEmailDomainCustom("");
    setAddress("");
    setExtraAddress("");
  };

  return (
    <fieldset className="border border-gray-300 rounded-lg p-6 mb-10 bg-white">
      <legend className="text-lg font-semibold px-1">회사 등록</legend>

      {/* ------------------------- 참여 매핑 등록 ------------------------- */}
      <div className="border border-gray-200 rounded-lg p-5 mb-8 bg-gray-50">
        <div className="text-base font-semibold mb-4">프로젝트 참여 매핑</div>

        {/* 역할 선택 + 주관사 여부 + 추가 버튼 */}
        <div className="flex items-center gap-3 mb-4">
          <select
            value={roleToAdd}
            onChange={(e) => setRoleToAdd(e.target.value)}
            className="border rounded-md px-3 py-2 bg-white text-sm w-48"
          >
            <option value="">역할 선택 *</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={roleLeadFlag}
              onChange={(e) => setRoleLeadFlag(e.target.checked)}
              className="h-4 w-4"
            />
            주관사 여부
          </label>

          <button
            type="button"
            onClick={() => {
              if (!roleToAdd) return alert("역할을 선택하세요.");

              if (selectedRoles.some((r) => r.id === Number(roleToAdd))) {
                alert("이미 추가된 역할입니다.");
                return;
              }

              setSelectedRoles((prev) => [
                ...prev,
                {
                  id: Number(roleToAdd),
                  name: roles.find((r) => r.id == roleToAdd)?.name,
                  is_lead: roleLeadFlag,
                },
              ]);

              setRoleToAdd("");
              setRoleLeadFlag(false);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md"
          >
            추가
          </button>
        </div>

        {/* 선택된 역할 pill 리스트 */}
        <div className="flex flex-wrap gap-2 mb-6">
          {selectedRoles.length === 0 && (
            <div className="text-sm text-gray-500">선택한 역할이 없습니다.</div>
          )}
          {selectedRoles.map((role) => (
            <div
              key={role.id}
              className="flex items-center gap-2 bg-white border border-gray-300 px-3 py-1.5 rounded-md"
            >
              <span className="text-sm text-gray-700">
                {role.name} {role.is_lead && "(주관)"}
              </span>
              <button
                onClick={() =>
                  setSelectedRoles((prev) =>
                    prev.filter((r) => r.id !== role.id)
                  )
                }
                className="text-gray-400 hover:text-red-500 text-xs font-bold"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        {/* 날짜 입력 */}
        <div className="flex gap-6">
          <div className="flex-1">
            <label className="text-sm text-gray-600 block mb-1">
              참여 시작일
            </label>
            <input
              type="date"
              className="border rounded-md px-3 py-2 w-full text-sm"
              value={pcpForm.start_date || projectPeriod?.start_date}
              onChange={(e) =>
                setPcpForm((f) => ({ ...f, start_date: e.target.value }))
              }
            />
          </div>
          <div className="flex-1">
            <label className="text-sm text-gray-600 block mb-1">
              참여 종료일
            </label>
            <input
              type="date"
              className="border rounded-md px-3 py-2 w-full text-sm"
              value={pcpForm.end_date || projectPeriod?.end_date}
              onChange={(e) =>
                setPcpForm((f) => ({ ...f, end_date: e.target.value }))
              }
            />
          </div>
        </div>
      </div>

      {/* ------------------------- 회사 정보 입력 ------------------------- */}
      <div className="grid grid-cols-2 gap-3">
        <input
          type="text"
          placeholder="회사명 *"
          value={newCompany.name}
          onChange={onChangeNew("name")}
          className="border rounded-md px-3 py-2 w-full text-sm"
        />

        <input
          type="text"
          placeholder="사업자등록번호"
          value={newCompany.registration_no}
          onChange={onChangeNew("registration_no")}
          className="border rounded-md px-3 py-2 w-full text-sm"
        />

        {/* ---------------- 전화번호/이메일 구역 ---------------- */}
        <div className="col-span-2 grid grid-cols-2 gap-4">
          {/* 전화번호 */}
          <div className="grid grid-cols-3 gap-2">
            <select
              value={phonePrefix}
              onChange={(e) => setPhonePrefix(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm bg-white"
            >
              {PHONE_PREFIXES.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>

            <input
              type="text"
              inputMode="numeric"
              placeholder="번호 입력"
              value={displayedRest}
              onChange={(e) => setPhoneRest(digitsOnly(e.target.value))}
              className="border rounded-md px-3 py-2 col-span-2 text-sm"
            />
          </div>

          {/* 이메일 */}
          <div className="grid grid-cols-10 items-center gap-2">
            <input
              type="text"
              placeholder="이메일 아이디"
              value={emailLocal}
              onChange={(e) => setEmailLocal(e.target.value.replace(/\s/g, ""))}
              className="border rounded-md px-3 py-2 text-sm col-span-4"
            />

            <span className="text-center">@</span>

            <select
              value={emailDomainSel}
              onChange={(e) => setEmailDomainSel(e.target.value)}
              className="border rounded-md px-2 py-2 text-sm bg-white col-span-3"
            >
              {EMAIL_DOMAINS.map((d) => (
                <option key={d} value={d}>
                  {d === "__custom__" ? "직접 입력" : d}
                </option>
              ))}
            </select>

            {emailDomainSel === "__custom__" && (
              <input
                type="text"
                placeholder="example.com"
                value={emailDomainCustom}
                onChange={(e) =>
                  setEmailDomainCustom(e.target.value.replace(/\s/g, ""))
                }
                className="border rounded-md px-3 py-2 text-sm col-span-3"
              />
            )}
          </div>
        </div>

        {/* 주소 */}
        {/* <input
          type="text"
          placeholder="주소"
          value={newCompany.address}
          readOnly
          className="border rounded-md px-3 py-2 w-full text-sm"
        /> */}
        <input
          type="text"
          placeholder="주소(지번/도로명 입력 가능) *"
          value={newCompany.address || ""}
          onChange={(e) =>
            setNewCompany((prev) => ({ ...prev, address: e.target.value }))
          }
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />

        <DaumPostcode
          text="주소 검색"
          address={address}
          extraAddress={extraAddress}
          onAddressChange={({ address, extra_address }) => {
            setAddress(address);
            setExtraAddress(extra_address);
            setNewCompany((prev) => ({ ...prev, address, extra_address }));
          }}
        />

        <input
          type="text"
          placeholder="참고 주소"
          value={newCompany.extra_address}
          readOnly
          className="border rounded-md px-3 py-2 w-full text-sm"
        />

        <input
          id="detailAddress"
          type="text"
          placeholder="상세 주소"
          value={newCompany.detail_address}
          onChange={onChangeNew("detail_address")}
          className="border rounded-md px-3 py-2 w-full text-sm"
        />
      </div>

      {/* ----------------- 공장 등록 ----------------- */}
      {!newCompany.id && (
        <div className="mt-4 flex items-center gap-2">
          <input
            type="checkbox"
            checked={hasFactories}
            onChange={(e) => setHasFactories(e.target.checked)}
          />
          <span className="text-sm">공장 있음 (함께 등록)</span>
        </div>
      )}

      {hasFactories && !newCompany.id && (
        <div className="mt-5 border-t pt-4 space-y-3">
          {factoryRows.map((r, idx) => (
            <FactoryRow
              key={r.id ?? idx}
              row={r}
              idx={idx}
              newCompany={newCompany}
              setFactoryRows={setFactoryRows}
              onChangeFactoryRow={onChangeFactoryRow}
              removeFactoryRow={removeFactoryRow}
            />
          ))}

          <button
            type="button"
            onClick={addFactoryRow}
            className="bg-gray-700 hover:bg-gray-800 text-white px-3 py-2 text-sm rounded"
          >
            행 추가
          </button>
        </div>
      )}

      {/* ---------------- 제출 버튼 ---------------- */}
      <div className="mt-6 text-center">
        <button
          onClick={handleSubmit}
          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md text-sm"
        >
          {newCompany.id ? "저장" : "등록"}
        </button>
      </div>
    </fieldset>
  );
};

export default CompanyForm;
